import { MasterDataStore } from 'src/api/masterData'
import { CompanySearchConditionsDataParam } from 'src/api/types/needs'
import { MasterData } from 'src/models/MasterData'
import { Persona } from 'src/models/Persona'

export type NeedsQueryParam = {
  page?: number
  type_of_business_id?: string
  employee_se?: string
  needs?: string
  solutions?: string
  prefecture_id_se?: string
  position?: string
  capital?: string
  is_listed_company?: string
  founded_at_se?: string
  business_object_se?: string
  admin_age_se?: string
  needs_key?: string
  is_new?: string
  no_message?: string
  refuse_message?: string
  role_for_analysis?: string
  min_created_date?: string
  max_created_date?: string
  min_last_sign_in_at?: string
  max_last_sign_in_at?: string
  company_id?: string
}

export const toNeedsQueryParamFreeWord = (
  persona: Persona,
): NeedsQueryParam => {
  return {
    type_of_business_id: undefined,
    employee_se: undefined,
    needs: undefined,
    solutions: undefined,
    prefecture_id_se: undefined,
    position: undefined,
    capital: undefined,
    is_listed_company: undefined,
    founded_at_se: undefined,
    business_object_se: undefined,
    admin_age_se: undefined,
    role_for_analysis: undefined,
    needs_key: persona.freeWord ? persona.freeWord : undefined,
    is_new: undefined,
    no_message: undefined,
    refuse_message: undefined,
    min_created_date: undefined,
    max_created_date: undefined,
    min_last_sign_in_at: undefined,
    max_last_sign_in_at: undefined,
  }
}

export const toNeedsQueryParam = (persona: Persona): NeedsQueryParam => {
  return {
    type_of_business_id:
      persona.personaTypeOfBusinessIds.length > 0
        ? persona.personaTypeOfBusinessIds.join('|')
        : undefined,
    employee_se:
      persona.personaEmployeeNumberIds.length > 0
        ? persona.personaEmployeeNumberIds.join('|')
        : undefined,
    needs:
      persona.personaCompanyBuyNeedOptionIds.length > 0
        ? persona.personaCompanyBuyNeedOptionIds.join('|')
        : undefined,
    solutions:
      persona.personaSolutionIds.length > 0
        ? persona.personaSolutionIds.join('|')
        : undefined,
    prefecture_id_se:
      persona.personaPrefectureIds.length > 0
        ? persona.personaPrefectureIds.join('|')
        : undefined,
    position:
      persona.personaPositionIds.length > 0
        ? persona.personaPositionIds.join('|')
        : undefined,
    capital:
      persona.personaCapitalIds.length > 0
        ? persona.personaCapitalIds.join('|')
        : undefined,
    is_listed_company:
      persona.isListedCompany !== null
        ? persona.isListedCompany
          ? 'true'
          : 'false'
        : undefined,
    founded_at_se:
      persona.personaFoundedAtIds.length > 0
        ? persona.personaFoundedAtIds.join('|')
        : undefined,
    business_object_se:
      persona.personaBusinessObjectIds.length > 0
        ? persona.personaBusinessObjectIds.join('|')
        : undefined,
    admin_age_se:
      persona.personaAdminAgeIds.length > 0
        ? persona.personaAdminAgeIds.join('|')
        : undefined,
    role_for_analysis:
      persona.personaRoleForAnalysisIds.length > 0
        ? persona.personaRoleForAnalysisIds.join('|')
        : undefined,
    needs_key: undefined,
    is_new: persona.isNew ? '1' : persona.isNew === undefined ? undefined : '0',
    no_message: persona.noMessage
      ? '1'
      : persona.noMessage === undefined
      ? undefined
      : '0',
    refuse_message: persona.refuseMessage
      ? '1'
      : persona.refuseMessage === undefined
      ? undefined
      : '0',
    min_created_date: persona.minCreatedDate
      ? persona.minCreatedDate
      : undefined,
    max_created_date: persona.maxCreatedDate
      ? persona.maxCreatedDate
      : undefined,
    min_last_sign_in_at: persona.minLastSignInAt
      ? persona.minLastSignInAt
      : undefined,
    max_last_sign_in_at: persona.maxLastSignInAt
      ? persona.maxLastSignInAt
      : undefined,
  }
}

export const toNeedsQueryParamFromCompanySearchConditionsDataParam = (
  param: CompanySearchConditionsDataParam,
): NeedsQueryParam => {
  return {
    type_of_business_id: param.typeOfBusinessId,
    employee_se: param.employeeSe,
    needs: param.needs,
    solutions: param.solutions,
    prefecture_id_se: param.prefectureIdSe,
    position: param.position,
    capital: param.capital,
    is_listed_company: param.isListedCompany,
    founded_at_se: param.foundedAtSe,
    business_object_se: param.businessObjectSe,
    admin_age_se: param.adminAgeSe,
    needs_key: param.needsKey,
    is_new: param.isNew,
    no_message: param.noMessage,
    refuse_message: param.refuseMessage,
    role_for_analysis: param.roleForAnalysis,
    min_created_date: param.minCreatedDate,
    max_created_date: param.maxCreatedDate,
    min_last_sign_in_at: param.minLastSignInAt,
    max_last_sign_in_at: param.maxLastSignInAt,
  }
}

export const toPersona = (
  data: NeedsQueryParam,
  masterData: MasterDataStore,
): Persona => {
  const personaAdminAgeIds = data.admin_age_se
    ? data.admin_age_se
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaAdminAges = personaAdminAgeIds
    .map((value) => {
      const item = masterData.adminAges.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        adminAgeId: Number(item?.value),
        adminAge: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.adminAgeId - b.adminAgeId)

  const personaBusinessObjectIds = data.business_object_se
    ? data.business_object_se
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaBusinessObjects = personaBusinessObjectIds
    .map((value) => {
      const item = masterData.businessObjects.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        businessObjectId: Number(item?.value),
        businessObject: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.businessObjectId - b.businessObjectId)

  const personaEmployeeNumberIds = data.employee_se
    ? data.employee_se
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaEmployeeNumbers = personaEmployeeNumberIds
    .map((value) => {
      const item = masterData.numberOfEmployees.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        employeeNumberId: Number(item?.value),
        employeeNumber: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.employeeNumberId - b.employeeNumberId)

  const personaFoundedAtIds = data.founded_at_se
    ? data.founded_at_se
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaFoundedAts = personaFoundedAtIds
    .map((value) => {
      const item = masterData.foundedAts.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        foundedAtId: Number(item?.value),
        foundedAt: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.foundedAtId - b.foundedAtId)

  const personaPrefectureIds = data.prefecture_id_se
    ? data.prefecture_id_se
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaPrefectures = personaPrefectureIds
    .map((value) => {
      const item = masterData.prefectures.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        prefectureId: Number(item?.value),
        prefecture: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.prefectureId - b.prefectureId)

  const personaTypeOfBusinessIds = data.type_of_business_id
    ? data.type_of_business_id
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaTypeOfBusinesses = personaTypeOfBusinessIds
    .map((value) => {
      const item = masterData.businessTypes.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        typeOfBusinessId: Number(item?.value),
        typeOfBusiness: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.typeOfBusinessId - b.typeOfBusinessId)

  const personaCompanyBuyNeedOptionIds = data.needs
    ? data.needs
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaCompanyBuyNeedOptions = personaCompanyBuyNeedOptionIds
    .map((value) => {
      const item = masterData.needs.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        companyBuyNeedOptionId: Number(item?.value),
        companyBuyNeedOption: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.companyBuyNeedOptionId - b.companyBuyNeedOptionId)

  const personaSolutionIds = data.solutions
    ? data.solutions
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaSolutions = personaSolutionIds
    .map((value) => {
      const item = masterData.solutions.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: 0,
        solutionId: Number(item?.value),
        solution: {
          id: Number(item?.value),
          name: item?.label ?? '',
        },
      }
    })
    .sort((a, b) => a.solutionId - b.solutionId)

  const personaCapitalIds = data.capital
    ? data.capital
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaCapitals = personaCapitalIds
    .map((value) => {
      const item = masterData.capitalNumberOptions.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: Number(item?.value),
        name: item?.label ?? '',
      }
    })
    .sort((a, b) => a.id - b.id)

  const personaPositionIds = data.position
    ? data.position
        ?.split('|')
        .map((value) => Number(value))
        .filter((value) => {
          return value
        })
        .sort((a, b) => a - b)
    : []
  const personaPositions = personaPositionIds
    .map((value) => {
      const item = masterData.positionNumberOptions.find((item) => {
        return Number(item.value) === value
      })
      return {
        id: Number(item?.value),
        name: item?.label ?? '',
      }
    })
    .sort((a, b) => a.id - b.id)

  const personaRoleForAnalysisIds = data.role_for_analysis
    ? data.role_for_analysis
        ?.split('|')
        .map((value) => value)
        .filter((value) => {
          return value
        })
        .sort()
    : []
  const masterDataRoleForAnalysis: MasterData[] = [
    {
      label: '無料',
      value: 'free',
    },
    {
      label: '有料',
      value: 'paid',
    },
  ]
  const personaRoleForAnalysis = personaRoleForAnalysisIds.map((value) => {
    const item = masterDataRoleForAnalysis.find((item) => {
      return item.value === value
    })
    return {
      id: 0,
      roleForAnalysisId: item?.value ?? '',
      roleForAnalysis: {
        id: item?.value ?? '',
        name: item?.label ?? '',
      },
    }
  })

  return {
    id: 0,
    personaAdminAgeIds,
    personaBusinessObjectIds,
    personaEmployeeNumberIds,
    personaFoundedAtIds,
    personaPrefectureIds,
    personaTypeOfBusinessIds,
    personaCompanyBuyNeedOptionIds,
    personaSolutionIds,
    personaCapitalIds,
    personaPositionIds,
    personaRoleForAnalysisIds,
    refuseMessage:
      data.refuse_message === '1' || data.refuse_message === undefined
        ? true
        : false,
    noMessage:
      data.no_message === '1' || data.no_message === undefined ? true : false,
    isNew: data.is_new === '1' ? true : false,
    isListedCompany: data.is_listed_company
      ? data.is_listed_company === 'true'
        ? true
        : false
      : null,
    freeWord: data.needs_key || null,
    minCreatedDate: data.min_created_date || null,
    maxCreatedDate: data.max_created_date || null,
    minLastSignInAt: data.min_last_sign_in_at || null,
    maxLastSignInAt: data.max_last_sign_in_at || null,
    personaAdminAges,
    personaCompanyBuyNeedOptions,
    personaSolutions,
    personaBusinessObjects,
    personaEmployeeNumbers,
    personaFoundedAts,
    personaPrefectures,
    personaTypeOfBusinesses,
    personaCapitals,
    personaPositions,
    personaRoleForAnalysis,
  }
}
